/**
 * Every place a plan is born goes through here: the dashboard's chooser, the
 * landing page's hero and its occasion cards. A new plan takes the language the
 * planner is reading — the editor and the PDF follow `plan.language`, so a plan
 * made in English by a Russian visitor would print in English.
 */

import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../lib/api'
import { useLocale } from '../lib/i18n/I18nProvider'
import type { EventKind } from '../lib/types'

export function useCreatePlan(): {
  /** The kind being created, while it is. */
  creating: EventKind | null
  /** Creates the plan and opens it. Rejects with the storage error, if any. */
  create: (kind: EventKind) => Promise<void>
} {
  const navigate = useNavigate()
  const { locale } = useLocale()
  const [creating, setCreating] = useState<EventKind | null>(null)

  const create = useCallback(
    async (kind: EventKind) => {
      setCreating(kind)
      try {
        const plan = await api.createPlan({ eventKind: kind, language: locale, template: 'starter' })
        navigate(`/plan/${plan.id}`)
      } catch (error) {
        setCreating(null)
        throw error
      }
    },
    [locale, navigate],
  )

  return { creating, create }
}
